// auto-schedule-derive.ts — P4-3 deterministic day time-blocking (no AI).
// Places MIT tasks sequentially into the first free gaps of the work day,
// skipping intervals already occupied by existing events. Pure + testable.
import { minutesToTime, timeToMinutes } from "./calendar-week-grid-derive";

export type ScheduleInput = {
  /** MITs to place, in priority order. */
  mits: { id: string; title: string; estimateMinutes: number }[];
  /** Existing busy blocks today (start "HH:MM" + duration minutes). */
  busy: { start: string; durationMin: number }[];
  workStart?: string; // default 09:00
  workEnd?: string;   // default 18:00
  gapMin?: number;    // breather between blocks, default 0
};

export type ScheduleBlock = { taskId: string; title: string; time: string; durationMin: number };

/** Earliest start ≥ cursor where [start, start+dur] hits no busy interval; null if none before workEnd. */
function firstFreeSlot(cursor: number, dur: number, busy: [number, number][], endMin: number): number | null {
  let start = cursor;
  // Walk forward: whenever the candidate window overlaps a busy block, jump past it.
  // busy is sorted by start; loop until stable or out of range.
  for (let guard = 0; guard < busy.length + 1; guard++) {
    if (start + dur > endMin) return null;
    const clash = busy.find(([bs, be]) => start < be && start + dur > bs);
    if (!clash) return start;
    start = clash[1]; // jump to end of the clashing block
  }
  return start + dur <= endMin ? start : null;
}

export function autoScheduleDay(input: ScheduleInput): ScheduleBlock[] {
  const startMin = timeToMinutes(input.workStart ?? "09:00");
  const endMin = timeToMinutes(input.workEnd ?? "18:00");
  const gap = input.gapMin ?? 0;

  const busy: [number, number][] = input.busy
    .map((b) => [timeToMinutes(b.start), timeToMinutes(b.start) + Math.max(0, b.durationMin || 0)] as [number, number])
    .sort((a, b) => a[0] - b[0]);

  const blocks: ScheduleBlock[] = [];
  let cursor = startMin;
  for (const mit of input.mits) {
    const dur = Math.max(15, mit.estimateMinutes || 30);
    const slot = firstFreeSlot(cursor, dur, busy, endMin);
    if (slot === null) break; // no room left in the work day
    blocks.push({ taskId: mit.id, title: mit.title, time: minutesToTime(slot), durationMin: dur });
    busy.push([slot, slot + dur]);
    busy.sort((a, b) => a[0] - b[0]);
    cursor = slot + dur + gap;
  }
  return blocks;
}
